import { FunctionComponent, memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnalyticChartPlatform } from '../../../../services';
import { TabItem, Tabs } from '../../../../shared';
import { ChartType } from '../Analytics';
import { useChartExclude } from '../helpers';
import { ChannelList } from './ChannelList';
import { CommentTop } from './CommentTop';
import { ContentActivity } from './ContentActivity';
import { ContentInteraction } from './ContentInteraction';
import { ContentList } from './ContentList';
import { ContentTagList } from './ContentTagList';
import { ContentTop } from './ContentTop';
import { InAppNotificationList } from './InAppNotificationList';
import { PushNotificationList } from './PushNotificationList';
import { StoryList } from './StoryList';

import classes from '../Analytics.module.scss';

enum AnalyticsContentTabType {
  CONTENT_1,
  CONTENT_2,
  CONTENT_3,
}

export interface ContentProps {
  from: string;
  to: string;
  channelId?: number[];
  platform?: AnalyticChartPlatform[];
  categoryId?: number[];
  organisationTags?: {
    externalTagsId: (number | null)[];
    internalTagsId: (number | null)[];
    externalStatusTagsId: (number | null)[];
  };
}

export const Content: FunctionComponent<ContentProps> = memo((contentProps) => {
  const { t: ctxT } = useTranslation('', { keyPrefix: 'analyticsContentTabs' });

  const [selectedTabIndex, setSelectedTabIndex] = useState(AnalyticsContentTabType.CONTENT_1);

  const excludeContentActivity = useChartExclude(ChartType.CONTENT_AND_ENGAGEMENT);
  const excludeContentInteraction = useChartExclude(ChartType.CONTENT_WITH_INTERACTIONS);
  const excludeContentTop = useChartExclude(ChartType.CONTENT_TOP_5);
  const excludeCommentTop = useChartExclude(ChartType.COMMENT_TOP_5);
  const excludeContentList = useChartExclude(ChartType.CONTENT);
  const excludeContentTagList = useChartExclude(ChartType.CONTENT_TAGS);
  const excludeChannelList = useChartExclude(ChartType.CHANNELS);
  const excludeStorylList = useChartExclude(ChartType.MIXES);
  const excludePushNotificationsList = useChartExclude(ChartType.PUSH_NOTIFICATIONS);
  const excludeInAppNotificationsList = useChartExclude(ChartType.IN_APP_NOTIFICATIONS);

  const tabItems: TabItem[] = useMemo(
    () => [
      {
        index: AnalyticsContentTabType.CONTENT_1,
        name: ctxT('content-1'),
        content: (
          <div className={classes['analytics__tab-content']}>
            {!excludeContentActivity && <ContentActivity {...contentProps} />}
            {!excludeContentInteraction && <ContentInteraction {...contentProps} />}
            {!excludeContentTop && <ContentTop {...contentProps} />}
            {!excludeCommentTop && <CommentTop {...contentProps} />}
            {!excludeContentList && <ContentList {...contentProps} />}
            {!excludeContentTagList && <ContentTagList {...contentProps} />}
          </div>
        ),
      },
      {
        index: AnalyticsContentTabType.CONTENT_2,
        name: ctxT('content-2'),
        content: (
          <div className={classes['analytics__tab-content']}>
            {!excludeChannelList && <ChannelList {...contentProps} />}
            {!excludeStorylList && <StoryList {...contentProps} />}
          </div>
        ),
      },
      {
        index: AnalyticsContentTabType.CONTENT_3,
        name: ctxT('content-3'),
        content: (
          <div className={classes['analytics__tab-content']}>
            {!excludePushNotificationsList && <PushNotificationList {...contentProps} />}
            {!excludeInAppNotificationsList && <InAppNotificationList {...contentProps} />}
          </div>
        ),
      },
    ],
    [
      contentProps,
      ctxT,
      excludeChannelList,
      excludeCommentTop,
      excludeContentActivity,
      excludeContentInteraction,
      excludeContentList,
      excludeContentTagList,
      excludeContentTop,
      excludeInAppNotificationsList,
      excludePushNotificationsList,
      excludeStorylList,
    ]
  );

  return (
    <Tabs
      items={tabItems}
      selectedTabIndex={selectedTabIndex}
      setSelectedTabIndex={setSelectedTabIndex}
      noBackground
    />
  );
});
